import { filterPublicReviewImages, type ReviewImage } from './reviewImages';
import { canCreateCpmSubmissionAppeal } from './canCreateCpmSubmissionAppeal';
import { canRespondToCpmSubmissionAppeal } from './canRespondToCpmSubmissionAppeal';

export type AppealRowUi =
  | { kind: 'hidden' }
  | { kind: 'contest' }
  | { kind: 'pending'; argument: string; evidenceImageUrls: string[] }
  | { kind: 'kept'; reply: string; reviewerImages: ReviewImage[] }
  | {
    kind: 'needsMoreInfo';
    reply: string;
    argument: string;
    evidenceImageUrls: string[];
    reviewerImages: ReviewImage[];
    followUpDisabled: boolean;
  }
  | { kind: 'accepted'; reply: string };

function evidenceImageUrlsFrom(
  appeal: { evidence_image_urls?: string[] | null } | null | undefined,
): string[] {
  return Array.isArray(appeal?.evidence_image_urls)
    ? appeal.evidence_image_urls.filter((url): url is string => typeof url === 'string' && !!url)
    : [];
}

export function resolveAppealRowUi({
  campaignStatus,
  submission,
  appeal,
  now,
}: {
  campaignStatus: string | null | undefined;
  submission: {
    admin_approval?: string | null;
    disqualified?: boolean | null;
    approval_reason?: string | null;
    content_review_group_id?: string | null;
    disapproved_at?: string | Date | null;
  };
  appeal: {
    status?: string | null;
    argument?: string | null;
    reviewer_reply?: string | null;
    evidence_image_urls?: string[] | null;
    reviewer_images?: unknown;
  } | null;
  now: Date;
}): AppealRowUi {
  // Approved submissions never show contest/pending UI, even if a stale
  // pending appeal object is still in client state after auto-close.
  if (submission.admin_approval === 'approved') {
    const reply = appeal?.reviewer_reply?.trim();
    if (reply) return { kind: 'accepted', reply };
    return { kind: 'hidden' };
  }
  if (appeal?.status === 'pending') {
    return {
      kind: 'pending',
      argument: appeal.argument || '',
      evidenceImageUrls: evidenceImageUrlsFrom(appeal),
    };
  }
  if (appeal?.status === 'kept') {
    return {
      kind: 'kept',
      reply: appeal.reviewer_reply || '',
      reviewerImages: filterPublicReviewImages(appeal.reviewer_images),
    };
  }
  if (appeal?.status === 'needs_more_info') {
    return {
      kind: 'needsMoreInfo',
      reply: appeal.reviewer_reply || '',
      argument: appeal.argument || '',
      evidenceImageUrls: evidenceImageUrlsFrom(appeal),
      reviewerImages: filterPublicReviewImages(appeal.reviewer_images),
      followUpDisabled: !canRespondToCpmSubmissionAppeal({
        campaign: { status: campaignStatus },
        submission,
        appeal,
      }).ok,
    };
  }
  const eligible = canCreateCpmSubmissionAppeal({
    campaign: { status: campaignStatus },
    submission: {
      admin_approval: submission.admin_approval,
      disqualified: !!submission.disqualified,
      approval_reason: submission.approval_reason,
      content_review_group_id: submission.content_review_group_id,
      disapproved_at: submission.disapproved_at,
    },
    now,
    existingForCycle: appeal && appeal.status !== 'accepted' ? appeal : null,
  });
  return eligible.ok ? { kind: 'contest' } : { kind: 'hidden' };
}
